/**
 * MIC QR payload format — the single place where the string is built and read.
 *
 *   MIC-QR-<ENTITY>-<id>$KEY:value*KEY2:value2
 *
 * e.g. MIC-QR-FABRICATIONORDER-94$FABRICATIONORDER:OF-B*PARTREFERENCE:PART1
 *
 * The payload carries the resolvable INSTANCE identity plus the fields marked "QR" in the
 * template. It is version-independent: template versions only decide which selection you load.
 * Entity names and field keys are ONE uppercase string with no separators (PART, RAWMATERIAL,
 * FABRICATIONORDER, ORDER, TOOL, FIXTURE).
 *
 * Everything is UPPERCASE and restricted to the QR "alphanumeric" charset (0-9 A-Z space $%*+-./:), so
 * qrcodegen encodes the label in ALPHANUMERIC mode (~5.5 bits/char) instead of BYTE (8) → a sparser,
 * easier-to-scan code. That is why building uppercases the whole payload and maps '_' → '-': lossy on
 * purpose, since identity resolves by id and the embedded values are informative only.
 *
 * Both sides live here so the generator (QrCodeGeneratorDialog) and the reader (QrScannerDialog)
 * can never drift apart.
 */

export const QR_PREFIX = 'MIC-QR-'
const DATA_SEPARATOR = '$' // ends the identity, opens the field list
const FIELD_SEPARATOR = '*'
const VALUE_SEPARATOR = ':'

// Wire form of an entity name or a field key: 'RawMaterial' → 'RAWMATERIAL', 'client.code' → 'CLIENTCODE'.
const wireKey = (name) => String(name).replace(/\./g, '').toUpperCase()

// The QR is read by a human who scans it, not parsed as a URL: values stay literal (spaces, '-', '/'…).
// Only '*' is escaped, the one separator a value could use to fake an extra field ('%2A' is itself inside
// the alphanumeric set). '$' needs no escaping: the identity ends at the FIRST '$', so a later one is
// just part of a value. ':' neither, because a pair is split on its FIRST ':' only, so an ISO datetime
// (DATE:2026-07-22T12:46) survives untouched. '%' is left alone on purpose: '50% COTTON' must stay
// readable on the printed label.
const escapeValue = (value) => String(value).replace(/\*/g, '%2A')
const unescapeValue = (value) => value.replace(/%2A/g, '*')

/**
 * Builds the QR string for one instance.
 *
 * @param {object} params
 * @param {string} params.entityType e.g. 'Part' | 'RawMaterial' — emitted uppercase
 * @param {number|string} params.id instance id
 * @param {Array<{field: string, value: string}>} params.entries selected QR fields, already formatted.
 *   EVERY entry is emitted, even with an empty value (`KEY:`) — the selection is the contract, so a label
 *   reprinted from the same template always yields the same key set.
 * @returns {string}
 */
export const buildQrPayload = ({ entityType, id, entries = [] }) => {
  // Key from the field PATH (not the translated label): a nested `relation.prop` → the relation name,
  // a flat field → itself. Auto-disambiguate ONLY when two selected fields share a relation
  // (client.code + client.name → CLIENTCODE / CLIENTNAME).
  //
  // ═══ ADDENDUM 34 (E40) — LA CLAVE DEPENDE DE LA SELECCIÓN, Y SE QUEDA ASÍ A PROPÓSITO ═══
  //
  // La desambiguación mira `entries`, o sea lo que el usuario MARCÓ, no lo que la pantalla ofrece.
  // Consecuencia: el mismo campo viaja bajo claves distintas según qué otros campos se marcaran al
  // lado. Medido ejecutando esta misma función:
  //
  //     client.code SOLO                   ->  CLIENT:
  //     client.code + client.name          ->  CLIENTCODE:  +  CLIENTNAME:
  //     partReference.code SOLO            ->  PARTREFERENCE:
  //     partReference.code + .comment      ->  PARTREFERENCECODE:  +  PARTREFERENCECOMMENT:
  //     lineOrder.lineNumber SOLO          ->  LINEORDER:
  //     lineOrder.lineNumber + .targetDate ->  LINEORDERLINENUMBER:  +  LINEORDERTARGETDATE:
  //
  // ⚠️ ESTO YA OCURRE ENTRE DOS ETIQUETAS DE LA MISMA VENTANA, con selecciones distintas. NO es una
  // regresión de la ficha de detalle: es un comportamiento, y es anterior a ella. Se descubrió al
  // comparar el listado de órdenes con la ficha nueva, pero la ficha solo lo hizo visible.
  //
  // ⚠️ DECISIÓN DEL PO (2026-09-21): se deja como está. Cambiarlo —usar siempre el path completo, o
  // siempre la relación— invalidaría la lectura POR CLAVE de etiquetas que pueden estar pegadas en
  // piezas ahí fuera, sin saber cuántas ni dónde, y afectaría a las SEIS entidades que usan este
  // diálogo. Si algún día molesta, será historia propia y con los clientes avisados.
  //
  // 📌 Y LO QUE NO CIERRA EL TRABAJO DE S381, para que nadie lea "ya está igualado": igualar las
  // listas de campos de las dos ventanas NO elimina estas divergencias. El listado ofrece
  // `partReference.comment` y `lineOrder.targetDate`, que no resuelven sobre el DTO del detalle y
  // por eso no entraron en los nueve campos añadidos; en cuanto alguien los marque allí, la
  // desambiguación se activa y las claves vuelven a discrepar. Lo único que S381 sí cerró es la
  // divergencia de PATH (`code` frente a `fabricationOrder`), que sí era nuestra.
  const bases = entries.map(entry => entry.field.includes('.') ? entry.field.split('.')[0] : entry.field)
  const keyOf = (entry, i) => bases.filter(base => base === bases[i]).length > 1 ? wireKey(entry.field) : wireKey(bases[i])
  const data = entries
    .map((entry, i) => `${keyOf(entry, i)}${VALUE_SEPARATOR}${escapeValue(entry.value)}`)
    .join(FIELD_SEPARATOR)
  const suffix = entries.length === 0 ? '' : DATA_SEPARATOR + data
  // '_' is common in codes but outside the QR alphanumeric charset, so it would drop that value to byte
  // mode; map it and uppercase last, so keys, values and escapes all land inside the charset.
  return `${QR_PREFIX}${wireKey(entityType)}-${id}${suffix}`.replace(/_/g, '-').toUpperCase()
}

// Entity names carry no separators, so the head is unambiguous. Case-insensitive on purpose: a payload
// typed by hand on a terminal should still resolve; the entity is normalised to uppercase on the way out.
const HEAD_PATTERN = new RegExp(`^${QR_PREFIX}([A-Z0-9]+)-(\\d+)$`, 'i')

// Anything that is not a MIC QR: a plain code typed by hand, a supplier barcode, an unrelated QR.
// The raw text is still returned so the caller can resolve it its own way.
const nonMicResult = (raw) => ({ raw, entityType: '', id: 0, fields: {}, isMicQr: false })

const parseFields = (data) => {
  if (data === '') return {}
  return data.split(FIELD_SEPARATOR).reduce((fields, pair) => {
    if (pair === '') return fields
    // Split on the FIRST ':' only: ':' is not escaped, so a value may legitimately contain it.
    const separator = pair.indexOf(VALUE_SEPARATOR)
    const key = separator === -1 ? pair : pair.slice(0, separator)
    const value = separator === -1 ? '' : pair.slice(separator + 1)
    if (key !== '') fields[key.toUpperCase()] = unescapeValue(value)
    return fields
  }, {})
}

/**
 * Reads a scanned string. Never throws: unrecognised input comes back as `isMicQr: false`
 * with the raw text, so the caller decides what to do with it.
 *
 * @param {string} rawInput scanned/typed text
 * @returns {{raw: string, entityType: string, id: number, fields: object, isMicQr: boolean}}
 *   entityType and the field keys come back uppercase; values verbatim.
 */
export const parseQrPayload = (rawInput = '') => {
  // Hardware scanners append the terminator and sometimes padding whitespace.
  const raw = String(rawInput || '').trim()
  if (raw === '' || !raw.toUpperCase().startsWith(QR_PREFIX)) return nonMicResult(raw)

  const dataStart = raw.indexOf(DATA_SEPARATOR)
  const head = dataStart === -1 ? raw : raw.slice(0, dataStart)
  const data = dataStart === -1 ? '' : raw.slice(dataStart + 1)

  const match = HEAD_PATTERN.exec(head)
  if (!match) return nonMicResult(raw)

  return {
    raw,
    entityType: match[1].toUpperCase(),
    id: Number(match[2]),
    fields: parseFields(data),
    isMicQr: true
  }
}
